import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Building2, Check, CheckCircle2, Edit3, ExternalLink, Globe, Loader2, MapPin, School, Search, ShieldCheck, Trash2, Users, X } from "lucide-react";
import { toast } from "react-toastify";
import Sidebar from "../components/layout/Sidebar";
import {
  schoolDirectoryService,
  type DirectoryStatusFilter,
  type SchoolDirectoryDetail,
  type SchoolDirectoryEntry,
  type UpdateSchoolDirectoryPayload,
} from "../services/schoolDirectoryService";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_TABS: { key: DirectoryStatusFilter; label: string }[] = [
  { key: "unverified", label: "Unverified" },
  { key: "verified", label: "Verified" },
  { key: "all", label: "All" },
];

// Display labels for the directory `source` provenance. These are
// operator-facing, so "llm" reads as "Auto-matched" rather than leaking
// the internal jargon — the stored source value is unchanged.
const SOURCE_LABELS: Record<string, { label: string; tone: string }> = {
  user: { label: "User-typed", tone: "bg-gray-100 text-gray-700" },
  llm: { label: "Auto-matched", tone: "bg-amber-100 text-amber-800" },
  admin: { label: "Admin", tone: "bg-blue-100 text-blue-800" },
};

function SourceBadge({ source }: { source: string }) {
  const { label, tone } = SOURCE_LABELS[source] ?? { label: source, tone: "bg-gray-100 text-gray-700" };
  return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide ${tone}`}>{label}</span>;
}

// Distinguishes entries already graduated into an operational school (they
// show under Partner Schools) from candidate names that are still directory-
// only. This is the crux of the "why don't the two tabs match?" question:
// the directory is a superset of candidate names; only linked ones are
// managed schools.
function ManagedBadge({ linked }: { linked: boolean }) {
  return linked ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-800">
      <Building2 className="w-3 h-3" />
      Managed school
    </span>
  ) : (
    <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500">Candidate</span>
  );
}

function locationLine(entry: Pick<SchoolDirectoryEntry, "city" | "region" | "country">): string {
  return [entry.city, entry.region, entry.country].filter(Boolean).join(", ");
}

// ─── Edit modal ───────────────────────────────────────────────────────────

interface EditModalProps {
  entry: SchoolDirectoryEntry;
  onClose: () => void;
  onSaved: (updated: SchoolDirectoryEntry) => void;
}

function EditModal({ entry, onClose, onSaved }: EditModalProps) {
  const [name, setName] = useState(entry.name);
  const [city, setCity] = useState(entry.city ?? "");
  const [region, setRegion] = useState(entry.region ?? "");
  const [country, setCountry] = useState(entry.country ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      // Build a delta — only send fields the admin actually changed. Empty
      // strings are sentinel "set to null" on the server (city/region/country
      // are nullable). Name has a min length, never empty.
      const payload: UpdateSchoolDirectoryPayload = {};
      if (name.trim() !== entry.name) payload.name = name.trim();
      if (city !== (entry.city ?? "")) payload.city = city;
      if (region !== (entry.region ?? "")) payload.region = region;
      if (country !== (entry.country ?? "")) payload.country = country;

      const updated = await schoolDirectoryService.update(entry.id, payload);
      toast.success("Saved");
      onSaved(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Edit directory entry</h2>
            <p className="text-sm text-gray-500 mt-0.5">Fix typos before verifying. Empty fields clear them.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Name" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              maxLength={200}
              autoFocus
            />
          </Field>
          <Field label="City">
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              placeholder="(empty to clear)"
              maxLength={120}
            />
          </Field>
          <Field label="Region / State / Province">
            <input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              placeholder="(empty to clear)"
              maxLength={80}
            />
          </Field>
          <Field label="Country (ISO-2)" hint="e.g. CA, US, GB">
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary uppercase"
              maxLength={2}
              placeholder="(empty to clear)"
            />
          </Field>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || name.trim().length < 2}
            className="flex-1 px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-[#3974d8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700 flex items-baseline gap-1.5">
        {label}
        {required && <span className="text-red-500 text-xs">*</span>}
        {hint && <span className="text-xs text-gray-400 font-normal">— {hint}</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ─── Detail modal ───────────────────────────────────────────────────────────

interface DetailModalProps {
  entry: SchoolDirectoryEntry;
  onClose: () => void;
  onEdit: (entry: SchoolDirectoryEntry) => void;
  onVerify: (entry: SchoolDirectoryEntry) => void;
  onDelete: (entry: SchoolDirectoryEntry) => void;
  isPending: boolean;
}

function DetailModal({ entry, onClose, onEdit, onVerify, onDelete, isPending }: DetailModalProps) {
  const [detail, setDetail] = useState<SchoolDirectoryDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    schoolDirectoryService
      .getDetail(entry.id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entry.id]);

  const location = locationLine(entry);
  const website = detail?.website ?? null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900">{entry.name}</h2>
              {entry.verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-green-100 text-green-800">
                  <ShieldCheck className="w-3 h-3" />
                  Verified
                </span>
              )}
              <SourceBadge source={entry.source} />
              <ManagedBadge linked={!!entry.linkedSchoolId} />
            </div>
            <div className="flex items-center gap-3 mt-1.5 text-sm text-gray-500">
              {location ? (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {location}
                </span>
              ) : (
                <span className="text-gray-400 italic">No location</span>
              )}
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <Globe className="w-3.5 h-3.5" />
                  Website
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Linked students */}
        <div className="flex-1 overflow-y-auto p-6 pt-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-3">
            <Users className="w-4 h-4 text-gray-400" />
            Linked students
            <span className="text-gray-400 font-normal">({entry.studentCount.toLocaleString()})</span>
          </h3>

          {detail?.linkedSchoolId && (
            <div className="mb-3 text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              This entry has been graduated into an operational school.
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          ) : !detail || detail.students.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-4">No students are linked to this entry yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {detail.students.map((s) => (
                <li key={s.id} className="px-3 py-2.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-gray-500 uppercase">
                    {(s.name ?? s.email).slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.name ?? s.email}</p>
                    <p className="text-xs text-gray-500 truncate">{s.email}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 text-xs text-gray-500">
                    {s.grade && <span className="px-1.5 py-0.5 rounded bg-gray-100">Grade {s.grade}</span>}
                    {s.status !== "ACTIVE" && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{s.status}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {detail && detail.students.length < entry.studentCount && (
            <p className="text-xs text-gray-400 mt-2">
              Showing {detail.students.length} of {entry.studentCount.toLocaleString()} linked students.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 p-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => onEdit(entry)}
            disabled={isPending}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
          >
            <Edit3 className="w-4 h-4" />
            Edit
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => onDelete(entry)}
            disabled={isPending}
            className="px-3 py-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
          {!entry.verified && (
            <button
              type="button"
              onClick={() => onVerify(entry)}
              disabled={isPending}
              className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 font-medium text-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Verify
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

export default function SchoolDirectoryPage() {
  // Default to "All" so a just-verified row stays visible (flipping to its
  // Verified badge in place) instead of dropping out of an Unverified filter
  // — which read as the entry "disappearing". Backend sorts unverified-first,
  // so the work queue still surfaces at the top of the All view.
  const [status, setStatus] = useState<DirectoryStatusFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState<SchoolDirectoryEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SchoolDirectoryEntry | null>(null);
  // The entry whose detail panel (linked students) is open, if any.
  const [detailEntry, setDetailEntry] = useState<SchoolDirectoryEntry | null>(null);
  // Rows that were just verified and are flashing their green "Verified"
  // state before being dropped from the unverified queue — makes success
  // unmistakable instead of the row silently vanishing.
  const [justVerifiedIds, setJustVerifiedIds] = useState<Set<string>>(new Set());
  // Per-row pending state so the Verify/Delete buttons spinner the right row
  // (multiple admins can fire actions in parallel without a global flag
  // disabling everything).
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  // Debounce search input → filter. Triggers a fresh first-page load.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Race-safety: every load() call bumps a token; only the latest is allowed
  // to mutate state. Without this, switching status quickly (or typing fast)
  // can leave the previous filter's results visible after the new request
  // returns slower.
  const loadTokenRef = useRef(0);

  const load = useCallback(
    async (replaceList: boolean, cursor?: string) => {
      const myToken = ++loadTokenRef.current;
      if (replaceList) {
        setIsLoading(true);
        setError(null);
      } else {
        setIsLoadingMore(true);
      }
      try {
        const result = await schoolDirectoryService.list({
          status,
          search: search || undefined,
          cursor,
          limit: PAGE_SIZE,
        });
        if (myToken !== loadTokenRef.current) return; // superseded
        setEntries((prev) => (replaceList ? result.entries : [...prev, ...result.entries]));
        setNextCursor(result.nextCursor);
        setTotal(result.total);
      } catch (e) {
        if (myToken !== loadTokenRef.current) return;
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (myToken === loadTokenRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [status, search],
  );

  // First-page load on filter / search change.
  useEffect(() => {
    void load(true);
  }, [load]);

  const handleVerify = async (entry: SchoolDirectoryEntry) => {
    setPendingIds((prev) => new Set(prev).add(entry.id));
    try {
      const result = await schoolDirectoryService.verify(entry.id);
      const verifiedRow: SchoolDirectoryEntry = result.entry ?? { ...entry, verified: true, source: "admin" };
      toast.success(`Verified ${entry.name}`);
      // Patch the row to its verified state in place so the green "Verified"
      // badge appears immediately — clear success feedback regardless of tab.
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? verifiedRow : e)));
      // Detail panel (if open on this entry) should reflect the new state too.
      setDetailEntry((cur) => (cur && cur.id === entry.id ? verifiedRow : cur));

      if (status === "unverified") {
        // On the unverified queue the row no longer matches the filter. Flash
        // the verified state briefly, then drop it — so the admin sees it
        // succeed rather than the row vanishing the instant they click.
        setJustVerifiedIds((prev) => new Set(prev).add(entry.id));
        window.setTimeout(() => {
          setEntries((prev) => prev.filter((e) => e.id !== entry.id));
          setTotal((t) => Math.max(0, t - 1));
          setJustVerifiedIds((prev) => {
            const next = new Set(prev);
            next.delete(entry.id);
            return next;
          });
        }, 1400);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Verify failed");
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
    }
  };

  const handleDelete = async (entry: SchoolDirectoryEntry) => {
    if (!window.confirm(`Delete "${entry.name}"? This can't be undone.`)) return;
    setPendingIds((prev) => new Set(prev).add(entry.id));
    try {
      await schoolDirectoryService.delete(entry.id);
      toast.success(`Deleted ${entry.name}`);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      setTotal((t) => Math.max(0, t - 1));
      setDetailEntry((cur) => (cur && cur.id === entry.id ? null : cur));
    } catch (e) {
      // 409 with a hint about linked users lands here — toast surfaces it.
      toast.error(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
    }
  };

  const handleSaved = (updated: SchoolDirectoryEntry) => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    // Keep the detail panel (which reopens once the edit modal closes) in sync.
    setDetailEntry((cur) => (cur && cur.id === updated.id ? updated : cur));
    setEditing(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-Manrope">
      <Sidebar />
      <main className="ml-[230px] p-8">
        <div className="max-w-6xl mx-auto">
          <header className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">School directory</h1>
            <p className="text-gray-500 mt-1">
              School names students entered at sign-up — separate from <span className="font-medium text-gray-600">Partner Schools</span>, so the counts differ.
              Verifying marks a name as trusted in the sign-up search; it doesn't onboard the school.
            </p>
          </header>

          {/* Filter pills + search */}
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3 shadow-sm">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setStatus(tab.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    status === tab.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {tab.label}
                  {status === tab.key && total > 0 && <span className="ml-2 text-xs text-gray-400">({total.toLocaleString()})</span>}
                </button>
              ))}
            </div>
            <div className="flex-1 min-w-[240px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary text-sm"
              />
            </div>
          </div>

          {/* Body */}
          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Couldn't load directory</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          ) : entries.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
              <School className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {search ? "No entries match this search." : status === "unverified" ? "Inbox zero — no unverified entries right now." : "No entries here yet."}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <ul className="divide-y divide-gray-100">
                  {entries.map((entry) => {
                    const isPending = pendingIds.has(entry.id);
                    const isJustVerified = justVerifiedIds.has(entry.id);
                    const location = locationLine(entry);
                    return (
                      <li
                        key={entry.id}
                        onClick={() => setDetailEntry(entry)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setDetailEntry(entry);
                          }
                        }}
                        className={`p-4 flex items-start gap-4 cursor-pointer transition-colors ${
                          isJustVerified ? "bg-green-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <School className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900 truncate">{entry.name}</span>
                            {entry.verified && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-green-100 text-green-800">
                                <ShieldCheck className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                            <SourceBadge source={entry.source} />
                            <ManagedBadge linked={!!entry.linkedSchoolId} />
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            {location ? (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {location}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">No location</span>
                            )}
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              {entry.studentCount.toLocaleString()} student{entry.studentCount === 1 ? "" : "s"}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditing(entry);
                            }}
                            disabled={isPending}
                            className="px-2.5 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {!entry.verified && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVerify(entry);
                              }}
                              disabled={isPending}
                              className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 font-medium text-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
                            >
                              {isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                              Verify
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(entry);
                            }}
                            disabled={isPending}
                            className="px-2.5 py-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {nextCursor && (
                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    onClick={() => void load(false, nextCursor)}
                    disabled={isLoadingMore}
                    className="px-5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {isLoadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Detail panel — hidden while the edit modal is open (Edit is launched
          from within it and would otherwise stack on top). */}
      {detailEntry && !editing && (
        <DetailModal
          entry={detailEntry}
          isPending={pendingIds.has(detailEntry.id)}
          onClose={() => setDetailEntry(null)}
          onEdit={(entry) => setEditing(entry)}
          onVerify={(entry) => handleVerify(entry)}
          onDelete={(entry) => handleDelete(entry)}
        />
      )}

      {editing && <EditModal entry={editing} onClose={() => setEditing(null)} onSaved={handleSaved} />}
    </div>
  );
}
